import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Input,
  Form,
  Alert,
  Table,
  message,
  Popconfirm,
  Space,
  Tag,
  Divider,
  Typography,
} from "antd";
import {
  generateHRISApiKey,
  revokeHRISApiKey,
  getHRISEmployees,
  getAllApiKeys,
} from "../../../service/hrisService";
import moment from "moment";
import PropTypes from "prop-types";
import { CopyOutlined, DeleteOutlined, SyncOutlined } from "@ant-design/icons";

const { Text } = Typography;

const HRISIntegration = ({ visible, onCancel }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKey, setApiKey] = useState(""); // For displaying newly generated key
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [keyForm] = Form.useForm();

  useEffect(() => {
    if (visible) {
      fetchApiKeys();
    }
  }, [visible]);

  const fetchApiKeys = async () => {
    try {
      const { apiKeys } = await getAllApiKeys();
      setApiKeys(apiKeys);
    } catch (error) {
      message.error("Failed  to fetch API keys");
    }
  };

  const copyToClipboard = () => {
    if (!apiKey) return;
    navigator.clipboard
      .writeText(apiKey)
      .then(() => message.success("API key copied to clipboard"))
      .catch(() => message.error("Failed to copy API key"));
  };

  const generateKey = async (values) => {
    setLoading(true);
    try {
      const response = await generateHRISApiKey({
        name: values.name,
        description: values.description || "HRIS Integration Key",
        expiresAt: moment().add(1, "year").toISOString(),
      });
      setApiKey(response.apiKey.key);
      message.success("API key generated successfully");
      fetchApiKeys();
      keyForm.resetFields();
    } catch (error) {
      message.error("Failed to generate API key");
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (keyValue) => {
    try {
      await revokeHRISApiKey(keyValue);
      message.success("API key revoked successfully");
      fetchApiKeys();
      if (apiKey === keyValue) setApiKey("");
    } catch (error) {
      message.error("Failed to revoke API key");
    }
  };

  const fetchEmployees = async () => {
    setSyncLoading(true);
    try {
      const data = await getHRISEmployees(lastSync);
      setEmployees(data.staff_list || []);
      setLastSync(data.last_record_updated);
      message.success(`Fetched ${data.staff_list.length} employees`);
    } catch (error) {
      message.error(`Failed to fetch employees from HRIS: ${error.message}`);
    } finally {
      setSyncLoading(false);
    }
  };

  const columns = [
    { title: "EPF No", dataIndex: "EPF_No", key: "EPF_No" },
    {
      title: "Name",
      key: "name",
      render: (_, record) => `${record.First_Name} ${record.Last_Name}`,
    },
    { title: "Department", dataIndex: "Department", key: "Department" },
    { title: "Designation", dataIndex: "Designation", key: "Designation" },
    { title: "Status", dataIndex: "Status", key: "Status" },
  ];

  const apiKeyColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Key",
      dataIndex: "key",
      key: "key",
      render: (text) => (
        <Space>
          <Text copyable={{ text }} style={{ maxWidth: 150 }} ellipsis>
            {text}
          </Text>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "status",
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>{isActive ? "Active" : "Revoked"}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Popconfirm
            title="Are you sure to revoke16 to revoke this API key?"
            onConfirm={() => handleRevoke(record.key)}
            okText="Yes"
            cancelText="No"
            disabled={!record.isActive}
          >
            <Button type="text" danger icon={<DeleteOutlined />} disabled={!record.isActive} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title="API Keys"
      visible={visible}
      onCancel={onCancel}
      width={1000}
      footer={null}
      zIndex={1200}
    >
      <div className="row">
        <div className="col-lg-12">
          <Form form={keyForm} layout="vertical" onFinish={generateKey} style={{ marginBottom: 24 }}>
            <Form.Item
              label="Key Name"
              name="name"
              rules={[{ required: true, message: "Please input key name!" }]}
            >
              <Input placeholder="e.g., HRIS Production Key" />
            </Form.Item>
            <Form.Item label="Description" name="description">
              <Input.TextArea rows={2} placeholder="Optional description" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Generate New API Key
              </Button>
            </Form.Item>
          </Form>

          {apiKey && (
            <div style={{ marginBottom: 24 }}>
              <Alert
                message="New API Key Generated"
                description={
                  <>
                    <Input value={apiKey} readOnly style={{ width: 300 }} />
                    <Button icon={<CopyOutlined />} onClick={copyToClipboard}>
                      Copy
                    </Button>
                </>
                }
                type="success"
                showIcon
              />
            </div>
          )}

          <Table
            columns={apiKeyColumns}
            dataSource={apiKeys}
            rowKey="key"
            pagination={{ pageSize: 5 }}
            style={{ marginBottom: 24 }}
            title={() => "Existing API Keys"}
          />

          <Divider />

          <h5>
            <SyncOutlined /> Employee Sync
          </h5>
          <div className="mb-4">
            <p>Last Sync: {lastSync || "Never"}</p>
            <Button type="primary" onClick={fetchEmployees} loading={syncLoading}>
              Fetch Employees from HRIS
            </Button>
          </div>

          {employees.length > 0 && (
            <Table
              columns={columns}
              dataSource={employees}
              rowKey="EPF_No"
              pagination={{ pageSize: 5 }}
              style={{ marginBottom: 20 }}
            />
          )}

          <Alert
            message="Important Notes"
            description={
              <>
                <p>- After integration, employee data will be managed in the HRIS system</p>
                <p>- EPF numbers must match between systems for proper synchronization</p>
                <p>- Changes made in HRIS will overwrite local employee data</p>
              </>
            }
            type="info"
            showIcon
            style={{ marginTop: 20 }}
          />
        </div>
      </div>
    </Modal>
  );
};

HRISIntegration.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default HRISIntegration;